import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ReactComponent as ArrowIcon } from 'assets/icons/arrowLeft.svg'
import { ReportIcon } from '../ReportIcon/ReportIcon'
import s from './ReportsShell.module.scss'

export const ReportsShell = ({
	title,
	icon,
	description,
	backTo,
	actions,
	badge,
	controls,
	children,
}) => {
	const navigate = useNavigate()
	const [downloadNotice, setDownloadNotice] = useState(null)
	const [showCompactControls, setShowCompactControls] = useState(false)
	const pageRef = useRef(null)
	const headerRef = useRef(null)

	useEffect(() => {
		let timeout
		const onDownload = event => {
			setDownloadNotice(event.detail)
			clearTimeout(timeout)
			timeout = setTimeout(() => setDownloadNotice(null), 4200)
		}
		window.addEventListener('kandor-report-download', onDownload)
		return () => { window.removeEventListener('kandor-report-download', onDownload); clearTimeout(timeout) }
	}, [])

	useEffect(() => {
		const page = pageRef.current
		if (!page || !controls) return undefined
		const update = () => {
			const headerHeight = headerRef.current?.offsetHeight || 0
			setShowCompactControls(page.scrollTop >= Math.max(headerHeight - 8, 1))
		}
		page.addEventListener('scroll', update, { passive: true })
		update()
		return () => page.removeEventListener('scroll', update)
	}, [controls])

	return (
		<div className={s['reports-shell']} ref={pageRef}>
			<div
				className={`${s['reports-shell__header']} ${controls ? s['reports-shell__header--with-controls'] : ''}`.trim()}
				ref={headerRef}
			>
				<div className={s['reports-shell__header-main']}>
					{backTo && (
						<button type="button" className={s['back-btn']} onClick={() => navigate(backTo)}>
							<ArrowIcon className={s['back-btn__icon']} />
							<span className={s['back-btn__label']}>Back</span>
						</button>
					)}
					<div className={s['title-row']}>
						{icon && (
							<span className={s['page-title-icon']} aria-hidden="true">
								{icon}
							</span>
						)}
						<h1 className={s['page-title']}>{title}</h1>
						{badge && <span className={s['preview-badge']}>{badge}</span>}
					</div>
					{description && <p className={s['page-description']}>{description}</p>}
				</div>
				{actions && <div className={s['reports-shell__actions']}>{actions}</div>}
				{controls && (
					<div className={s['reports-shell__controls']}>
						{React.isValidElement(controls)
							? React.cloneElement(controls, { variant: 'header' })
							: controls}
					</div>
				)}
			</div>

			{controls && showCompactControls && (
				<div className={s['compact-controls']}>
					{backTo && (
						<button
							type="button"
							className={s['compact-controls__back']}
							onClick={() => navigate(backTo)}
						>
							<ArrowIcon className={s['compact-controls__back-icon']} />
							<span className={s['compact-controls__back-label']}>Back</span>
						</button>
					)}
					<div className={s['compact-controls__toolbar']}>
						{React.isValidElement(controls)
							? React.cloneElement(controls, { variant: 'compact' })
							: controls}
					</div>
				</div>
			)}

			<div className={s['reports-shell__body']}>{children}</div>

			{downloadNotice && (
				<div className={s['download-toast']} role="status">
					<ReportIcon name="check_circle" className={s['download-toast__icon']} />
					<div>
						<strong className={s['download-toast__title']}>Download ready</strong>
						<span className={s['download-toast__subtitle']}>
							{downloadNotice.filename} · {downloadNotice.rowCount} rows
						</span>
					</div>
				</div>
			)}
		</div>
	)
}
